// Settings → Import tab.
//
// Two things live here:
//   1. A landing view with template downloads + history of past imports
//      (each with a Revert action).
//   2. A 3-step wizard modal (upload → map → done) that talks to
//      /api/imports/preview and /api/imports/commit.
//
// AI-suggested column mapping comes back on `preview` — we just render it
// as the initial mapping state, and let the user override with PragmaSelect.

import { useEffect, useMemo, useState } from "react";
import { Upload, Download, RotateCcw, CheckCircle2, AlertCircle, Sparkles, X, FileText, ArrowRight, Loader2 } from "lucide-react";
import api, { API_BASE, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import PragmaSelect from "../components/PragmaSelect";
import { useConfirm } from "../components/ConfirmModal";

const ENTITIES = [
  { key: "leads",     label: "Leads",     help: "Prospects in your lead inbox" },
  { key: "contacts",  label: "Contacts",  help: "People you've already qualified" },
  { key: "companies", label: "Companies", help: "Accounts your contacts and deals belong to" },
];

const DUP_STRATEGIES = [
  { value: "skip",           label: "Skip duplicates",   hint: "Do nothing if a row matches an existing record." },
  { value: "update",         label: "Update existing",   hint: "Overwrite fields on the existing record. Revertable." },
  { value: "create_anyway",  label: "Create anyway",     hint: "Add every row as new, even if a match exists." },
];

// ---------------------------------------------------------------------------
// Small local helpers
// ---------------------------------------------------------------------------
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

// ---------------------------------------------------------------------------
// Landing tab
// ---------------------------------------------------------------------------
export default function ImportsTab() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const confirm = useConfirm();

  const loadBatches = async () => {
    try {
      const { data } = await api.get("/imports");
      setBatches(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load import history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBatches(); }, []);

  const downloadTemplate = async (entity) => {
    try {
      const token = localStorage.getItem("pragma_token");
      const res = await fetch(`${API_BASE}/imports/template/${entity}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pragma-${entity}-template.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const revertBatch = async (b) => {
    const ok = await confirm({
      title: `Revert this import?`,
      message: `This will delete ${b.created_count} record(s) that were created${b.updated_count ? ` and restore ${b.updated_count} record(s) that were updated` : ""}. This cannot be undone.`,
      confirmLabel: "Revert import",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/imports/${b.id}/revert`);
      toast.success(`Reverted — ${data.deleted} deleted, ${data.restored} restored`);
      loadBatches();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Revert failed");
    }
  };

  return (
    <div className="max-w-4xl" data-testid="imports-tab">
      <h3 className="mb-2" style={{ fontSize: 18 }}>Import data from CSV</h3>
      <p className="text-sm mb-6" style={{ color: "var(--pg-text-mute)" }}>
        Bring your leads, contacts, and companies into Pragma. We'll match your columns
        automatically using AI, and you can revert any import that didn't go the way you wanted.
      </p>

      {/* Templates */}
      <div className="pg-card p-6 mb-6" data-testid="imports-templates">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={16} style={{ color: "var(--pg-accent)" }} />
          <h4 style={{ fontSize: 14, fontWeight: 600 }}>Not sure where to start? Grab a template</h4>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--pg-text-mute)" }}>
          Download a CSV with the right columns already set up, fill it in with your data in Excel or
          Google Sheets, then come back and upload.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {ENTITIES.map((e) => (
            <button
              key={e.key}
              onClick={() => downloadTemplate(e.key)}
              data-testid={`import-template-${e.key}`}
              className="text-left p-4 transition-colors"
              style={{
                background: "var(--pg-surface-alt, transparent)",
                border: "1px solid var(--pg-border)",
                borderRadius: 8,
                cursor: "pointer",
              }}
              onMouseEnter={(ev) => ev.currentTarget.style.borderColor = "var(--pg-accent)"}
              onMouseLeave={(ev) => ev.currentTarget.style.borderColor = "var(--pg-border)"}
            >
              <div className="flex items-center justify-between mb-1">
                <span style={{ fontSize: 14, fontWeight: 500 }}>{e.label}</span>
                <Download size={13} style={{ color: "var(--pg-text-mute)" }} />
              </div>
              <span className="text-xs" style={{ color: "var(--pg-text-mute)" }}>{e.help}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Start new import */}
      <div className="pg-card p-6 mb-6" data-testid="imports-start-card">
        <div className="flex items-center justify-between">
          <div>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Start a new import</h4>
            <p className="text-sm" style={{ color: "var(--pg-text-mute)", margin: 0 }}>
              Upload any CSV — from HubSpot, Salesforce, a spreadsheet, anywhere. Our AI matches your columns for you.
            </p>
          </div>
          <button
            className="pg-btn pg-btn-primary"
            onClick={() => setWizardOpen(true)}
            data-testid="imports-start-btn"
          >
            <Upload size={14} /> New import
          </button>
        </div>
      </div>

      {/* History */}
      <div className="pg-card overflow-hidden" data-testid="imports-history">
        <div className="p-4" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Recent imports</h4>
        </div>
        {loading ? (
          <div className="p-8 text-sm text-center" style={{ color: "var(--pg-text-mute)" }}>Loading…</div>
        ) : batches.length === 0 ? (
          <div className="p-8 text-sm text-center" style={{ color: "var(--pg-text-mute)" }} data-testid="imports-history-empty">
            You haven't imported anything yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--pg-border)", color: "var(--pg-text-mute)" }}>
                <th className="text-left p-3 font-medium">When</th>
                <th className="text-left p-3 font-medium">Entity</th>
                <th className="text-left p-3 font-medium">File</th>
                <th className="text-left p-3 font-medium">Result</th>
                <th className="text-left p-3 font-medium">By</th>
                <th className="text-right p-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} style={{ borderBottom: "1px solid var(--pg-border)" }} data-testid={`imports-row-${b.id}`}>
                  <td className="p-3" style={{ color: "var(--pg-text-mute)" }}>{fmtDate(b.created_at)}</td>
                  <td className="p-3 capitalize">{b.entity}</td>
                  <td className="p-3 truncate" style={{ maxWidth: 220 }}>{b.filename}</td>
                  <td className="p-3">
                    {b.status === "reverted" ? (
                      <span style={{ color: "var(--pg-text-mute)", fontSize: 12 }}>reverted · {fmtDate(b.reverted_at)}</span>
                    ) : (
                      <span style={{ fontSize: 12 }}>
                        <span style={{ color: "var(--pg-green, #16a34a)" }}>+{b.created_count} created</span>
                        {b.updated_count > 0 && <>, <span style={{ color: "var(--pg-accent)" }}>{b.updated_count} updated</span></>}
                        {b.skipped_count > 0 && <>, <span style={{ color: "var(--pg-text-mute)" }}>{b.skipped_count} skipped</span></>}
                      </span>
                    )}
                  </td>
                  <td className="p-3" style={{ color: "var(--pg-text-mute)" }}>{b.user_name}</td>
                  <td className="p-3 text-right">
                    {b.status === "reverted" ? (
                      <span style={{ color: "var(--pg-text-mute)", fontSize: 11 }}>—</span>
                    ) : (
                      <button
                        onClick={() => revertBatch(b)}
                        className="pg-btn pg-btn-ghost"
                        style={{ fontSize: 12, padding: "4px 10px" }}
                        data-testid={`imports-revert-${b.id}`}
                      >
                        <RotateCcw size={12} /> Revert
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {wizardOpen && (
        <ImportWizard
          onClose={() => setWizardOpen(false)}
          onDone={() => { setWizardOpen(false); loadBatches(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------
function ImportWizard({ onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [entity, setEntity] = useState("leads");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // full preview response
  const [mapping, setMapping] = useState({});   // csv_header -> target_field
  const [dupStrategy, setDupStrategy] = useState("skip");
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("entity", entity);
      fd.append("file", file);
      const { data } = await api.post("/imports/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setStep(2);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not read the file");
    } finally {
      setUploading(false);
    }
  };

  const runCommit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/imports/commit", {
        stash_id: preview.stash_id,
        entity: preview.entity,
        mapping,
        duplicate_strategy: dupStrategy,
      });
      setResult(data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 200, background: "rgba(15,23,42,0.55)", animation: "fadeIn 160ms ease-out" }}
      data-testid="import-wizard"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          width: 780, maxWidth: "94vw", maxHeight: "88vh",
          background: "var(--pg-surface)", borderRadius: 14, border: "1px solid var(--pg-border)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.28)", overflow: "hidden",
          display: "flex", flexDirection: "column",
          animation: "invModalRise 220ms ease-out",
        }}
      >
        {/* Header */}
        <div className="p-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--pg-accent)" }}>
              Step {step} of 3
            </div>
            <h3 style={{ fontSize: 17, margin: "4px 0 0", fontWeight: 600 }}>
              {step === 1 && "Upload your CSV"}
              {step === 2 && "Match your columns"}
              {step === 3 && "Import complete"}
            </h3>
          </div>
          <button onClick={onClose} className="pg-btn pg-btn-ghost" data-testid="import-wizard-close" style={{ padding: 6 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: 24 }}>
          {step === 1 && (
            <StepUpload
              entity={entity}
              setEntity={setEntity}
              file={file}
              setFile={setFile}
            />
          )}
          {step === 2 && preview && (
            <StepMap
              preview={preview}
              mapping={mapping}
              setMapping={setMapping}
              dupStrategy={dupStrategy}
              setDupStrategy={setDupStrategy}
            />
          )}
          {step === 3 && result && (
            <StepResult result={result} />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 flex items-center justify-between" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button
            onClick={step === 1 ? onClose : () => setStep(step - 1)}
            className="pg-btn pg-btn-ghost"
            disabled={uploading || committing || step === 3}
            data-testid="import-wizard-back"
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>
          <div className="flex items-center gap-2">
            {step === 1 && (
              <button
                className="pg-btn pg-btn-primary"
                onClick={runPreview}
                disabled={!file || uploading}
                data-testid="import-wizard-next"
              >
                {uploading ? <><Loader2 size={13} className="animate-spin" /> Reading…</> : <>Continue <ArrowRight size={13} /></>}
              </button>
            )}
            {step === 2 && (
              <button
                className="pg-btn pg-btn-primary"
                onClick={runCommit}
                disabled={committing}
                data-testid="import-wizard-commit"
              >
                {committing ? <><Loader2 size={13} className="animate-spin" /> Importing…</> : <>Import {preview?.total_rows || 0} rows <ArrowRight size={13} /></>}
              </button>
            )}
            {step === 3 && (
              <button className="pg-btn pg-btn-primary" onClick={onDone} data-testid="import-wizard-done">
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Step 1: pick entity + upload file ------------------------------------
function StepUpload({ entity, setEntity, file, setFile }) {
  const [dragOver, setDragOver] = useState(false);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <div>
      <label className="block mb-2" style={{ fontSize: 12, color: "var(--pg-text-mute)" }}>
        What are you importing?
      </label>
      <div className="grid grid-cols-3 gap-2 mb-6">
        {ENTITIES.map((e) => (
          <button
            key={e.key}
            onClick={() => setEntity(e.key)}
            data-testid={`import-entity-${e.key}`}
            className="p-3 text-left transition-colors"
            style={{
              border: `1px solid ${entity === e.key ? "var(--pg-accent)" : "var(--pg-border)"}`,
              background: entity === e.key ? "rgba(91,79,232,0.06)" : "transparent",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{e.label}</div>
            <div className="text-xs" style={{ color: "var(--pg-text-mute)" }}>{e.help}</div>
          </button>
        ))}
      </div>

      <label className="block mb-2" style={{ fontSize: 12, color: "var(--pg-text-mute)" }}>
        Your CSV file
      </label>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        htmlFor="import-file-input"
        style={{
          display: "block",
          padding: "36px 20px",
          border: `2px dashed ${dragOver ? "var(--pg-accent)" : "var(--pg-border)"}`,
          borderRadius: 10,
          textAlign: "center",
          cursor: "pointer",
          background: dragOver ? "rgba(91,79,232,0.04)" : "transparent",
          transition: "border-color 140ms, background 140ms",
        }}
        data-testid="import-dropzone"
      >
        <Upload size={22} style={{ color: "var(--pg-text-mute)", margin: "0 auto 10px" }} />
        {file ? (
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{file.name}</div>
            <div className="text-xs mt-1" style={{ color: "var(--pg-text-mute)" }}>
              {(file.size / 1024).toFixed(1)} KB · click to choose a different file
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Drop your CSV here, or click to browse</div>
            <div className="text-xs mt-1" style={{ color: "var(--pg-text-mute)" }}>Up to 10 MB · max 5,000 rows</div>
          </>
        )}
        <input
          id="import-file-input"
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="import-file-input"
        />
      </label>
    </div>
  );
}

// ---- Step 2: map columns + choose dup strategy ---------------------------
function StepMap({ preview, mapping, setMapping, dupStrategy, setDupStrategy }) {
  const { headers, sample_rows, target_fields, ai_used, total_rows } = preview;

  // Compute which target field keys are still available so we don't map two
  // CSV columns to the same target.
  const usedTargets = useMemo(
    () => new Set(Object.values(mapping || {}).filter(Boolean)),
    [mapping]
  );

  const optionsFor = (currentValue) => {
    const opts = [{ value: "", label: "— Don't import —" }];
    target_fields.forEach((tf) => {
      const disabled = usedTargets.has(tf.key) && tf.key !== currentValue;
      opts.push({
        value: tf.key,
        label: tf.label + (tf.required ? " *" : ""),
        hint: tf.help,
        disabled,
      });
    });
    return opts;
  };

  const requiredFields = target_fields.filter((f) => f.required);
  const missingRequired = requiredFields.filter(
    (f) => !Object.values(mapping || {}).includes(f.key)
  );

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <span style={{ fontSize: 12, color: "var(--pg-text-mute)" }}>
          {total_rows} rows · matching to <strong style={{ color: "var(--pg-text)" }}>{preview.entity}</strong>
        </span>
        {ai_used && (
          <span
            className="mono text-[10px] uppercase tracking-wider"
            data-testid="import-ai-badge"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 8px", borderRadius: 100,
              background: "rgba(91,79,232,0.10)", color: "var(--pg-accent)",
            }}
          >
            <Sparkles size={10} /> AI-suggested mapping
          </span>
        )}
      </div>

      {/* Column mapping */}
      <div className="pg-card overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--pg-border)", color: "var(--pg-text-mute)" }}>
              <th className="text-left p-3 font-medium" style={{ width: "34%" }}>Your column</th>
              <th className="text-left p-3 font-medium" style={{ width: "30%" }}>Sample</th>
              <th className="text-left p-3 font-medium">Maps to</th>
            </tr>
          </thead>
          <tbody>
            {headers.map((h) => (
              <tr key={h} style={{ borderBottom: "1px solid var(--pg-border)" }} data-testid={`import-map-row-${h}`}>
                <td className="p-3" style={{ fontWeight: 500 }}>{h}</td>
                <td className="p-3 truncate" style={{ color: "var(--pg-text-mute)", maxWidth: 180 }}>
                  {(sample_rows[0] && sample_rows[0][h]) || <span className="italic">empty</span>}
                </td>
                <td className="p-3">
                  <PragmaSelect
                    value={mapping[h] || ""}
                    onChange={(v) => setMapping({ ...mapping, [h]: v || null })}
                    options={optionsFor(mapping[h])}
                    testId={`import-map-select-${h}`}
                    size="sm"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {missingRequired.length > 0 && (
        <div
          className="mb-4 p-3 flex items-start gap-2"
          style={{ background: "rgba(234,88,12,0.08)", border: "1px solid rgba(234,88,12,0.35)", borderRadius: 8 }}
          data-testid="import-missing-required"
        >
          <AlertCircle size={14} style={{ color: "var(--pg-orange, #ea580c)", marginTop: 2 }} />
          <div className="text-sm">
            <strong>Still needed:</strong> {missingRequired.map((f) => f.label).join(", ")}
          </div>
        </div>
      )}

      {/* Duplicate strategy */}
      <label className="block mb-2" style={{ fontSize: 12, color: "var(--pg-text-mute)" }}>
        When we find a match on {preview.entity === "companies" ? "company name" : "email"}
      </label>
      <PragmaSelect
        value={dupStrategy}
        onChange={setDupStrategy}
        options={DUP_STRATEGIES}
        testId="import-dup-strategy"
      />
    </div>
  );
}

// ---- Step 3: result summary ----------------------------------------------
function StepResult({ result }) {
  const total = (result.created_count || 0) + (result.updated_count || 0) + (result.skipped_count || 0);
  return (
    <div data-testid="import-result">
      <div className="flex items-center gap-3 mb-6">
        <div
          style={{
            width: 44, height: 44, borderRadius: "50%",
            background: "rgba(22,163,74,0.12)", color: "var(--pg-green, #16a34a)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <CheckCircle2 size={22} />
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Import complete</div>
          <div className="text-sm" style={{ color: "var(--pg-text-mute)" }}>
            Processed {total} rows from <strong>{result.filename}</strong>.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <ResultTile label="Created" value={result.created_count} tone="green" testid="import-result-created" />
        <ResultTile label="Updated" value={result.updated_count} tone="accent" testid="import-result-updated" />
        <ResultTile label="Skipped" value={result.skipped_count} tone="mute" testid="import-result-skipped" />
      </div>

      {result.error_count > 0 && (
        <div
          className="p-3 mb-4"
          style={{ background: "rgba(234,88,12,0.08)", border: "1px solid rgba(234,88,12,0.35)", borderRadius: 8 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={14} style={{ color: "var(--pg-orange, #ea580c)" }} />
            <strong className="text-sm">{result.error_count} row{result.error_count > 1 ? "s" : ""} could not be imported</strong>
          </div>
          <ul className="text-xs" style={{ color: "var(--pg-text-mute)", margin: 0, paddingLeft: 20 }}>
            {(result.errors || []).slice(0, 10).map((er, i) => (
              <li key={i}>Row {er.row}: {er.error}</li>
            ))}
            {result.error_count > 10 && <li>…and {result.error_count - 10} more</li>}
          </ul>
        </div>
      )}

      <p className="text-xs" style={{ color: "var(--pg-text-mute)" }}>
        Not what you expected? You can revert this import from the history table below.
      </p>
    </div>
  );
}

function ResultTile({ label, value, tone, testid }) {
  const color = tone === "green" ? "var(--pg-green, #16a34a)"
    : tone === "accent" ? "var(--pg-accent)"
    : "var(--pg-text-mute)";
  return (
    <div className="pg-card p-4 text-center" data-testid={testid}>
      <div style={{ fontSize: 26, fontWeight: 600, color }}>{value || 0}</div>
      <div className="mono text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--pg-text-mute)" }}>{label}</div>
    </div>
  );
}
